import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { CalendarData, Calendar_onValueChangeEvent } from '@inmoment/the-kitchen';

import { FilterService } from 'app/modules/collection/services/filter.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

export interface ActionItemListFilter {
  dueDate?: {
    start: Date;
    end: Date;
    relativeId?: string;
  };
  statuses?: string[];
  assignee?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-item-list-filter',
  templateUrl: './action-item-list-filter.component.html',
  styleUrls: ['./action-item-list-filter.component.scss'],
  standalone: false,
})
export class ActionItemListFilterComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Input() filter: ActionItemListFilter;
  @Input() isActionItem = true;

  @Output() modalClose = new EventEmitter<ActionItemListFilter | null>();

  form: UntypedFormGroup;
  calendarData: CalendarData;
  calendarRelativeId: string;
  calendarMode: 'fixed' | 'relative' = 'fixed';
  private calendarResultStart: Date;
  private calendarResultEnd: Date;

  constructor(formBuilder: UntypedFormBuilder, filterService: FilterService) {
    this.form = formBuilder.group({
      isDueDate: new UntypedFormControl(false),
      isNotStarted: new UntypedFormControl(false),
      isInProgress: new UntypedFormControl(false),
      isCompleted: new UntypedFormControl(false),
      assignee: new UntypedFormControl(''),
    });

    this.calendarData = {
      preselectedCalendarListOptions: filterService.generateRelativeRangeOptions(),
    };
  }

  ngOnInit(): void {
    if (this.filter) {
      this.form.patchValue({
        isDueDate: !!this.filter.dueDate,
        isNotStarted: this.filter.statuses?.includes('NEW'),
        isInProgress: this.filter.statuses?.includes('IN_PROGRESS'),
        isCompleted: this.filter.statuses?.includes('COMPLETED'),
        assignee: this.filter.assignee ?? '',
      });

      if (this.filter.dueDate) {
        if (this.filter.dueDate.relativeId) {
          this.calendarMode = 'relative';

          this.calendarRelativeId = this.filter.dueDate.relativeId;
        } else {
          this.calendarData.initialDateRange = {
            first: this.filter.dueDate.start,
            last: this.filter.dueDate.end,
          };
        }

        this.calendarResultStart = this.filter.dueDate.start;
        this.calendarResultEnd = this.filter.dueDate.end;
      }
    }
  }

  onCalendarValueChange(eventDetail: Calendar_onValueChangeEvent): void {
    this.calendarResultStart = eventDetail.dates.first;
    this.calendarResultEnd = eventDetail.dates.last;
    if (eventDetail.optionId) {
      this.calendarRelativeId = eventDetail.optionId;
    } else {
      this.calendarRelativeId = null;
    }
  }

  onApply(): void {
    const result: ActionItemListFilter = {};

    const addStatus = (controlValue: boolean, value: string) => {
      if (controlValue) {
        if (result.statuses) {
          result.statuses.push(value);
        } else {
          result.statuses = [value];
        }
      }
    };

    if (this.form.controls.isDueDate.value) {
      result.dueDate = {
        start: this.calendarResultStart,
        end: this.calendarResultEnd,
        relativeId: this.calendarRelativeId,
      };
    }

    addStatus(this.form.controls.isNotStarted.value, 'NEW');
    addStatus(this.form.controls.isInProgress.value, 'IN_PROGRESS');
    addStatus(this.form.controls.isCompleted.value, 'COMPLETED');

    const assignee = this.form.controls.assignee.value.trim();
    if (assignee) {
      result.assignee = assignee;
    }

    this.modalClose.emit(result);
  }

  onCancel(): void {
    this.modalClose.emit(null);
  }

  get isInvalid(): boolean {
    return (
      this.form.controls.isDueDate.value && (!this.calendarResultStart || !this.calendarResultEnd)
    );
  }
}
